import Serializable from "@/core/misc/mixins/Serializable";

/**
 * Опции для toString диапазона.
 *
 * Общий тип для базы и наследников. Три позиции в строке независимы и
 * собираются всегда одинаково: "{prefix} {start}{ middle }{end} {suffix}"
 * (пробелы только вокруг непустых частей).
 *
 *   prefix   — перед началом ("от", "с")
 *   middle   — между концами ("до", "по", "-", "—")
 *   suffix   — после конца ("включительно", "исключительно")
 *
 * Дополнительно (только числовые/строковые диапазоны базы):
 *   unit     — единица измерения после каждого значения ("°C", "кг")
 *   negative — текст для пустого диапазона ("Нет", "—")
 */
interface RangeToStringOptions {
  prefix?: string;
  middle?: string;
  suffix?: string;
  unit?: string;
  negative?: string;
}

/**
 * Абстрактный базовый класс для работы с диапазонами
 */
abstract class AbstractRange<T> extends Serializable {
  protected _start: T | null;
  protected _end: T | null;

  protected constructor(start: T | null = null, end: T | null = null) {
    super();
    if (new.target === AbstractRange) {
      throw new TypeError('Cannot construct AbstractRange instances directly');
    }

    this._start = start;
    this._end = end;

    this._validate();
  }

  /**
   * Валидация диапазона
   * @protected
   */
  protected _validate(): void {
    if (this._start && this._end && this._compareValues(this._start, this._end) > 0) {
      throw new Error('Start must be less than or equal to end');
    }
  }

  /**
   * Геттер для начала диапазона
   */
  get start(): T | null {
    return this._start;
  }

  /**
   * Сеттер для начала диапазона
   */
  set start(value: T | null) {
    this._start = value;
    this._validate();
  }

  /**
   * Геттер для конца диапазона
   */
  get end(): T | null {
    return this._end;
  }

  /**
   * Сеттер для конца диапазона
   */
  set end(value: T | null) {
    this._end = value;
    this._validate();
  }

  /**
   * Проверяет, является ли диапазон пустым
   */
  isEmpty(): boolean {
    return this._start === null && this._end === null;
  }

  /**
   * Проверяет, полностью ли задан диапазон (оба конца заданы)
   */
  isFilled(): boolean {
    return this._start !== null && this._end !== null;
  }

  /**
   * Проверяет равенство диапазонов
   */
  equals(other: AbstractRange<T>): boolean {
    if (!(other instanceof AbstractRange)) {
      return false;
    }
    return this._compareValues(this._start, other._start, true) && this._compareValues(this._end, other._end, true);
  }

  /**
   * Проверяет пересечение с другим диапазоном
   */
  overlaps(rightOperand: AbstractRange<T>): boolean {
    if (!(rightOperand instanceof AbstractRange)) {
      throw new Error('Can only check overlaps with another range');
    }
    if (this.isEmpty() || rightOperand?.isEmpty()) {
      return false;
    }
    return this._compareValues(this._start, rightOperand._end) <= 0 && this._compareValues(this._end, rightOperand._start) >= 0;
  }

  /**
   * Проверяет, содержит ли диапазон другой диапазон или значение
   */
  contains(rightOperand: AbstractRange<T> | T): boolean {
    if (this.isEmpty() || (rightOperand instanceof AbstractRange && rightOperand.isEmpty())) {
      return false;
    }

    const innerType = this._start ? typeof this._start : typeof this._end;

    if (rightOperand instanceof AbstractRange) {
      return this._compareValues(this._start, rightOperand._start) <= 0 && this._compareValues(this._end, rightOperand._end) >= 0;
    }
    else if (typeof rightOperand === innerType) {
      return this._compareValues(this._start, rightOperand as T) <= 0 && this._compareValues(this._end, rightOperand as T) >= 0;
    }
    else {
      throw new Error(`Can only check containment with another range or ${innerType}`);
    }
  }

  /**
   * Сравнивает значения
   * @protected
   */
  protected _compareValues(a: T | null, b: T | null, returnBoolean?: false): number;
  protected _compareValues(a: T | null, b: T | null, returnBoolean: true): boolean;
  protected _compareValues(a: T | null, b: T | null, returnBoolean: boolean = false): number | boolean {
    // Обработка null значений
    if (a === null && b === null) return returnBoolean ? true : 0;
    if (a === null) return returnBoolean ? false : -1;
    if (b === null) return returnBoolean ? false : 1;

    let result: number;

    // Если есть метод compareTo, используем его
    if (a && typeof (a as any).compareTo === 'function') {
      result = (a as any).compareTo(b);
    } else {
      // Для примитивных типов
      if (a < b) result = -1;
      else if (a > b) result = 1;
      else result = 0;
    }

    return returnBoolean ? result === 0 : result;
  }

  /**
   * Создает глубокую копию диапазона
   */
  clone(): AbstractRange<T> {
    return new (this.constructor as any)(this._start, this._end);
  }

  /**
   * Собирает строку диапазона из частей: "{prefix} {left} {middle} {right} {suffix}".
   * Пустые части (пустая строка или null) пропускаются, остальные склеиваются
   * через один пробел. Используется наследниками для единообразного оформления.
   * @protected
   */
  protected _joinParts(left: string | null, right: string | null, prefix: string | null, middle: string | null, suffix: string | null): string {
    const parts: string[] = [];

    for (const part of [prefix, left, middle, right, suffix]) {
      if (part !== null && part !== '') {
        parts.push(part);
      }
    }

    return parts.join(' ');
  }

  /**
   * Строковое представление диапазона.
   *
   * Параметры передаются единым объектом — это нужно для совместимости сигнатуры
   * с наследниками (например DateRange), у которых свой набор опций. Лишние поля
   * игнорируются, отсутствующие берут дефолт.
   *
   * Три позиции собираются как "{prefix} {start}{ middle }{end} {suffix}".
   *
   * @param options.prefix   слово перед началом. По умолчанию "от".
   * @param options.middle   слово между концами. По умолчанию "до".
   * @param options.suffix   слово после конца (например "включительно"). По умолчанию пусто.
   * @param options.unit     единица измерения после каждого значения ("°C", "кг"). Без пробела ставится прямо после числа.
   * @param options.negative что вернуть, если диапазон пуст ("Нет", "—"). По умолчанию пустая строка.
   *
   * Примеры:
   *   range.toString()                                  → "от 5 до 15"
   *   range.toString({ unit: '°C' })                    → "от 5 °C до 15 °C"
   *   range.toString({ negative: 'Нет' })               → "от 5 до 15", если пуст → "Нет"
   *   range.toString({ prefix: 'c', middle: 'по' })     → "c 5 по 15"
   */
  toString(options: RangeToStringOptions = {}): string {
    const prefix = options.prefix ?? 'от';
    const middle = options.middle ?? 'до';
    const suffix = options.suffix ?? '';
    const unit = options.unit ?? '';
    const negative = options.negative ?? '';

    let result: string;
    if (this.isEmpty()) {
      result = negative;
    }
    else {
      const unitSuffix = unit === '' ? '' : ` ${unit}`;
      const left = `${this._start ?? ''}${unitSuffix}`;
      const right = `${this._end ?? ''}${unitSuffix}`;
      result = this._joinParts(left, right, prefix, middle, suffix);
    }
    return result;
  }

  /**
   * Создание из объекта
   */
  static fromObject<T>(
      this: new (start: T | null, end: T | null) => AbstractRange<T>,
      obj: { start?: T | null; end?: T | null } | null
  ): AbstractRange<T> | null {

    if (!obj) return null;
    return new this(obj.start ?? null, obj.end ?? null);
  }

  static fromServer(abstractRange, result) {
      return super.fromServer(abstractRange, result);
  }
}

export default AbstractRange;
export type { RangeToStringOptions };